/**
 * Automatic gift reconciliation for the cart reward tiers.
 *
 * Keeps gift line items in sync with the cart:
 *   - Soak me (lube) : on every order that has a real item. Free because the
 *                      variant PRICE is 0.00 — no discount involved.
 *   - Gift (mystery) : only once the subtotal reaches Rs 4,999 AND the shopper
 *                      has applied MYSTERYGIFT. Zeroed by that Buy X Get Y code.
 * Either one is removed again the moment its condition stops holding.
 *
 * This file only decides whether a gift is IN the cart. What makes it free is
 * the variant price (Soak me) or the applied code (Gift) — see the notes on
 * GIFTS below before changing either.
 *
 * Thresholds here must match snippets/cart-tier-progress.liquid and the
 * `min_amount` of the matching row in settings.cart_coupon_data. Change them
 * together, or a shopper can hold a gift they are being charged for.
 *
 * Thresholds are in paise, matching the values the Shopify AJAX cart returns.
 */
(function () {
  'use strict';

  // Tier 1 is unconditional. Tier 3 is opt-in: the shopper must apply
  // MYSTERYGIFT before its gift is added. Tier 2 (Rs 3,999 / BDAY20) is a pure
  // price discount and adds no product at all.
  //
  // A gift must be published to the Online Store channel — a product that is
  // ACTIVE but unpublished fails here with a 422 "Cannot find variant".
  var GIFTS = [
    // "Soak me" (product 10224269492507, handle upselling-soak-me).
    // Free because its variant PRICE is 0.00, not because of any discount:
    // the checkout in front of this store ignores Shopify discounts entirely,
    // so a 100%-off discount never reaches the shopper. Do not "fix" this by
    // restoring a price and adding a discount.
    { id: 52157012312347, threshold: 0 },

    // "Gift" (product 10125406765339, handle gift), Rs 499, zeroed by the
    // MYSTERYGIFT Buy X Get Y code. Added ONLY once that code is applied, so a
    // shopper who never opts in never receives it.
    //
    // Chicken-and-egg worth knowing: MYSTERYGIFT reports applicable:false until
    // this line exists, because a Buy X Get Y has nothing to discount without
    // its Y in the cart. So the check below tests that the code is PRESENT, not
    // that it is applicable — requiring applicable would deadlock and the gift
    // would never be added.
    { id: 51655209779483, threshold: 499900, requiresCode: 'MYSTERYGIFT' },
  ];

  /**
   * Whether THIS file owns the opt-in mystery gift line.
   *
   * The checkout in front of this store (Fastrr) manages its own cart. If we add
   * the Gift line and the shopper then removes MYSTERYGIFT inside Fastrr, Fastrr
   * does not remove the line — it has no idea the line is a gift — it simply
   * stops discounting it and charges Rs 499. Only whichever side ADDED a line can
   * reliably remove it, so the gift must be owned by exactly one side, never both.
   *
   * Tested 2026-08-08: Fastrr does NOT add the gift, it only discounts a line
   * that is already there. So the theme has to own it — and the orphan charge is
   * defused instead by pricing the Gift variant at 0.00, exactly like Soak me.
   * An orphaned line left behind by removing the coupon in Fastrr is then worth
   * nothing and cannot bill the shopper.
   *
   * This is why the gift must stay at price 0.00. Restore a real price and every
   * shopper who removes the coupon inside Fastrr is charged for it.
   */
  var THEME_ADDS_MYSTERY_GIFT = true;

  if (!THEME_ADDS_MYSTERY_GIFT) {
    GIFTS = GIFTS.filter(function (g) { return !g.requiresCode; });
  }

  /**
   * Master switch: does this file put ANY gift into the cart?
   *
   * Off since 2026-08-10 — the gifts are handled at the checkout instead, so the
   * theme must not add them too. Both variants carry a real price again (Soak me
   * Rs 399, Gift Rs 399), and a gift line the checkout does not recognise as a
   * gift is simply a product the shopper is charged for. Same single-owner rule
   * as the note above: whichever side ADDS a gift line has to be the side that
   * removes it, and that side is now the checkout.
   *
   * Turning this back on requires putting both variants back to price 0.00
   * first — see the THEME_ADDS_MYSTERY_GIFT comment for why.
   */
  var THEME_ADDS_GIFTS = false;

  /**
   * Whether to also strip gift lines left in carts built before adding was
   * turned off. Deliberately OFF: this file identifies a gift purely by variant
   * id, and both variants are ordinary sellable products — Soak me in particular
   * is a Rs 399 upsell with its own product page. Draining by variant id cannot
   * tell a leftover auto-added line from one the shopper chose, so it would
   * empty a real purchase out of the cart with no explanation.
   *
   * The stale lines are survivable in comparison: with the lock style no longer
   * injected, the row keeps its normal remove button and the shopper can take it
   * out. Shopify carts expire on their own inside a couple of weeks.
   */
  var THEME_REMOVES_STRAY_GIFTS = false;

  // Nothing to do at all — leave the cart entirely alone, including the fetch
  // patch below, rather than shadowing window.fetch for no reason.
  var ENABLED = THEME_ADDS_GIFTS || THEME_REMOVES_STRAY_GIFTS;

  var GIFT_IDS = GIFTS.map(function (g) { return g.id; });

  /**
   * Is `code` attached to the cart? Presence only — see the deadlock note above.
   * Codes come back with stray whitespace often enough to be worth trimming.
   */
  function cartHasCode(cart, code) {
    var wanted = String(code).trim().toUpperCase();
    return (cart.discount_codes || []).some(function (entry) {
      return String(entry.code).trim().toUpperCase() === wanted;
    });
  }

  // Gift lines are always quantity 1 and are managed entirely by this file, so
  // the quantity stepper and remove button are hidden on them. Without this the
  // shopper can press + and watch it snap back to 1, which reads as broken.
  var LOCK_STYLE =
    '.cart-item--gift-locked quantity-input,' +
    '.cart-item--gift-locked .quantity,' +
    '.cart-item--gift-locked .quantity-popover-container,' +
    '.cart-item--gift-locked cart-remove-button{display:none !important;}' +
    '.cart-item--gift-locked .cart-item__quantity::after{' +
    'content:"Free gift \\00B7 limit 1";display:block;font-size:1.1rem;' +
    'color:rgba(var(--color-foreground),0.6);white-space:nowrap;}';

  // Sections to re-render after a change, so the drawer and cart page stay live.
  var SECTION_TARGETS = [
    { name: 'cart-drawer', selector: '.drawer__inner', into: '#CartDrawer .drawer__inner' },
    { name: 'cart-icon-bubble', selector: '.shopify-section', into: '#cart-icon-bubble' },
  ];

  var busy = false;

  function isGift(variantId) {
    return GIFT_IDS.indexOf(variantId) !== -1;
  }

  function injectLockStyle() {
    if (document.getElementById('cart-gift-lock-style')) return;
    var style = document.createElement('style');
    style.id = 'cart-gift-lock-style';
    style.textContent = LOCK_STYLE;
    document.head.appendChild(style);
  }

  /**
   * Flag every cart row holding a gift so the stylesheet can strip its quantity
   * controls. The theme re-renders cart sections constantly, so this has to run
   * again after each render — see the observer at the bottom.
   */
  function lockGiftRows() {
    var inputs = document.querySelectorAll('[data-quantity-variant-id]');
    for (var i = 0; i < inputs.length; i++) {
      var id = parseInt(inputs[i].getAttribute('data-quantity-variant-id'), 10);
      if (!isGift(id)) continue;
      var row = inputs[i].closest('.cart-item');
      if (row && !row.classList.contains('cart-item--gift-locked')) {
        row.classList.add('cart-item--gift-locked');
      }
    }
  }

  /**
   * Value of everything that is NOT a gift. Gifts are excluded so that adding
   * one cannot push the cart over the next threshold and unlock another.
   */
  function qualifyingSubtotal(cart) {
    return cart.items.reduce(function (sum, item) {
      return isGift(item.variant_id) ? sum : sum + item.original_line_price;
    }, 0);
  }

  function desiredQuantities(cart) {
    var subtotal = qualifyingSubtotal(cart);
    var hasRealItem = cart.items.some(function (item) { return !isGift(item.variant_id); });
    var updates = {};
    var changed = false;

    GIFTS.forEach(function (gift) {
      var currentQty = cart.items.reduce(function (n, item) {
        return item.variant_id === gift.id ? n + item.quantity : n;
      }, 0);
      // Never more than one of any gift per order, whatever the shopper does to
      // the quantity field — currentQty of 3 resolves back to 1 here.
      var wanted = THEME_ADDS_GIFTS && hasRealItem && subtotal >= gift.threshold ? 1 : 0;

      // Opt-in gifts also need their code applied. Removing the code, or the
      // cart dropping back under the threshold, takes the gift straight out
      // again — so the shopper can never keep a gift they no longer qualify for.
      if (wanted && gift.requiresCode && !cartHasCode(cart, gift.requiresCode)) {
        wanted = 0;
      }

      if (currentQty !== wanted) {
        updates[gift.id] = wanted;
        changed = true;
      }
    });

    return changed ? updates : null;
  }

  function currentSectionNames() {
    var names = SECTION_TARGETS.map(function (t) { return t.name; });
    // The cart page renders its items and footer as their own sections, whose
    // ids are dynamic — read them off the DOM when present.
    ['main-cart-items', 'main-cart-footer'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el && el.dataset.id) names.push(el.dataset.id);
    });
    return names;
  }

  function renderSections(sections) {
    if (!sections) return;

    SECTION_TARGETS.forEach(function (target) {
      var html = sections[target.name];
      var host = document.querySelector(target.into);
      if (!html || !host) return;
      var parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(target.selector);
      if (parsed) host.innerHTML = parsed.innerHTML;
    });

    ['main-cart-items', 'main-cart-footer'].forEach(function (id) {
      var el = document.getElementById(id);
      if (!el || !el.dataset.id) return;
      var html = sections[el.dataset.id];
      if (!html) return;
      var parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('.js-contents');
      var host = el.querySelector('.js-contents');
      if (parsed && host) host.innerHTML = parsed.innerHTML;
    });
  }

  function reconcile() {
    if (busy) return Promise.resolve();
    busy = true;

    return fetch(window.Shopify.routes.root + 'cart.js', {
      headers: { Accept: 'application/json' },
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var updates = desiredQuantities(cart);
        if (!updates) return null;

        return fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            updates: updates,
            sections: currentSectionNames().join(','),
            sections_url: window.location.pathname,
          }),
        }).then(function (res) {
          return res.json().then(function (body) {
            if (!res.ok) {
              // Shopify answers 422 with a useful reason — surface it instead of
              // failing silently. The usual cause is a gift product that is not
              // published to the Online Store sales channel, which reads as
              // "Cannot find variant" even though the variant id is correct.
              throw new Error(
                'cart/update.js ' + res.status + ': ' + (body.description || body.message || 'unknown')
              );
            }
            return body;
          });
        });
      })
      .then(function (result) {
        if (result && result.sections) renderSections(result.sections);
        if (THEME_ADDS_GIFTS) lockGiftRows();
      })
      .catch(function (err) {
        // Never let gift logic break the cart — log and move on.
        console.warn('[cart-gift-auto] reconcile failed', err);
      })
      .then(function () {
        busy = false;
      });
  }

  /**
   * Re-run whenever anything touches the cart. Patching fetch catches every
   * source — product forms, quick add, the drawer, and apps — rather than only
   * the events this theme happens to publish. Our own writes are skipped via
   * the `busy` flag, so this cannot feed itself.
   */
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?$/;

  // Applying a coupon does NOT go through /cart/*. The coupon UI calls
  // GET /discount/<code>, which changes what the cart qualifies for without ever
  // touching a cart write route. Miss this and an opt-in gift is never added:
  // the shopper presses Apply, the code attaches, and nothing else happens.
  var DISCOUNT_APPLY = /\/discount\//;

  var nativeFetch = window.fetch;
  if (ENABLED) window.fetch = function () {
    var url = arguments[0];
    var href = typeof url === 'string' ? url : url && url.url ? url.url : '';
    var path = String(href).split('?')[0];
    var isCartWrite = CART_WRITE.test(path) || DISCOUNT_APPLY.test(path);

    var call = nativeFetch.apply(this, arguments);
    if (!isCartWrite || busy) return call;

    return call.then(function (response) {
      // Let the theme finish handling its own response first.
      setTimeout(reconcile, 0);
      return response;
    });
  };

  /**
   * The theme replaces cart markup wholesale on every quantity change, which
   * drops our lock class. Re-apply after any DOM change, debounced so a burst
   * of section swaps costs one pass.
   */
  function watchForRerenders() {
    var pending = false;
    var observer = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      setTimeout(function () {
        pending = false;
        lockGiftRows();
      }, 60);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function start() {
    if (!ENABLED) return;

    // The lock hides the quantity stepper and the remove button on a gift row,
    // which is only right while the row is a free gift this file controls. With
    // adding off, any such row is a priced product the shopper must be able to
    // take out — so leave the controls alone.
    if (THEME_ADDS_GIFTS) {
      injectLockStyle();
      lockGiftRows();
      watchForRerenders();
    }

    reconcile();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
